import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  isWorkingDay,
  getDayName,
  nextWorkingDay,
  addWorkingDays,
  shiftToWorkingDay,
  formatDateFull,
  getDelayStatusColor,
  getDelayStatusLabel,
  getRiskLevelColor,
  getRiskLevelBg,
} from '@/lib/scheduleUtils';
import ScheduleItemModalLinksSection from './ScheduleItemModalLinksSection';
import ScheduleItemModalCreateLinkSection, {
  type PendingScheduleLinkDraft,
} from './ScheduleItemModalCreateLinkSection';
import ScheduleItemModalColorSection from './ScheduleItemModalColorSection';
import ScheduleItemModalChecklistSection from './ScheduleItemModalChecklistSection';
import ScheduleCompletionBlockModal from './ScheduleCompletionBlockModal';
import ScheduleDateInput from './ScheduleDateInput';
import NewInstructionModal from '@/components/feature/NewInstructionModal';
import type { ScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';

export interface FormState {
  title: string;
  phase: string;
  main_stage: string;
  construction_sub_stage: string;
  duration_days: number;
  start_date: string;
  end_date: string;
  progress: number;
  is_complete: boolean;
  is_included: boolean;
  assigned_to: string;
  assigned_role: string;
  notes: string;
  baseline_start_date: string;
  baseline_end_date: string;
  actual_start_date: string;
  actual_end_date: string;
  delay_reason: string;
  delay_status: ScheduleItem['delay_status'];
  schedule_risk_level: ScheduleItem['schedule_risk_level'];
  is_critical: boolean;
  bar_color: string | null;
  bar_color_mode: 'phase' | 'custom';
  gantt_color: string | null;
}

export interface ScheduleItemModalResult {
  form: FormState;
  changeReason: string;
  publishToClient: boolean;
  skipRecalcConfirm: boolean;
  action?: 'save' | 'create-task' | 'send-instruction';
  pendingLink?: PendingScheduleLinkDraft | null;
}

type SectionKey = 'basic' | 'dates' | 'links' | 'color' | 'notes' | 'checklist' | 'checklist-progress';

interface Props {
  show: boolean;
  editItem: ScheduleItem | null;
  links: ScheduleLink[];
  includedItems: ScheduleItem[];
  scheduleItems?: ScheduleItem[];
  projectId: string;
  onClose: () => void;
  onSave: (result: ScheduleItemModalResult) => void;
  onDelete?: (itemId: string) => void;
  onUpsertLink: (oldLinkId: string | null, payload: { predecessor_item_id: string; successor_item_id: string; link_type: 'FS' | 'SS' | 'FF' | 'SF'; lag_days: number }) => Promise<void>;
  onRemoveLink: (linkId: string) => Promise<void>;
  onRecalcLinkedItems: () => Promise<void>;
  hasSuccessors: boolean;
  saving: boolean;
  savingLink: boolean;
  deletingLinkId: string | null;
  saveError: string;
  saveSuccess: string;
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  canEdit: boolean;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  checklistProgress?: ScheduleChecklistProgress | null;
  onChecklistChanged?: () => void;
  createDefaults?: Partial<FormState> | null;
}

function SectionHeader({
  title,
  isOpen,
  onToggle,
  badge,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  badge?: string | number;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-2.5 cursor-pointer group"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-[#1A1A1A] tracking-wide">{title}</span>
        {badge !== undefined && badge !== 0 && (
          <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-1.5 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
        <i className={isOpen ? 'ri-arrow-up-s-line text-[#374151] text-sm' : 'ri-arrow-down-s-line text-[#374151] text-sm'}></i>
      </div>
    </button>
  );
}

export default function ScheduleItemModal({
  show,
  editItem,
  links,
  includedItems,
  scheduleItems,
  projectId,
  onClose,
  onSave,
  onDelete,
  onUpsertLink,
  onRemoveLink,
  onRecalcLinkedItems,
  hasSuccessors,
  saving,
  savingLink,
  deletingLinkId,
  saveError,
  saveSuccess,
  settings,
  holidays,
  canEdit,
  viewerRole = 'management',
  checklistProgress,
  onChecklistChanged,
  createDefaults = null,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({
    title: '', phase: '', main_stage: '', construction_sub_stage: '', duration_days: 1, start_date: '', end_date: '',
    progress: 0, is_complete: false, is_included: true, assigned_to: '', assigned_role: '', notes: '',
    baseline_start_date: '', baseline_end_date: '', actual_start_date: '', actual_end_date: '',
    delay_reason: '', delay_status: 'on_track', schedule_risk_level: 'normal', is_critical: false,
    bar_color: null, bar_color_mode: 'phase', gantt_color: null,
  });
  const [changeReason, setChangeReason] = useState('');
  const [publishToClient, setPublishToClient] = useState(false);
  const [startDateWarning, setStartDateWarning] = useState<string | null>(null);
  const [endDateWarning, setEndDateWarning] = useState<string | null>(null);
  const [showDurationRecalcConfirm, setShowDurationRecalcConfirm] = useState(false);
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set(['basic', 'color', 'dates']));
  const [showCompletionBlock, setShowCompletionBlock] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingCreateLink, setPendingCreateLink] = useState<PendingScheduleLinkDraft | null>(null);

  const isManagement = viewerRole === 'management';

  const checklistSectionProgress = checklistProgress ?? null;

  useEffect(() => {
    if (!show) return;
    if (editItem) {
      setForm({
        title: editItem.title,
        phase: editItem.phase || '',
        main_stage: editItem.main_stage || editItem.phase || '',
        construction_sub_stage: editItem.construction_sub_stage || '',
        duration_days: editItem.duration_days,
        start_date: editItem.start_date || '',
        end_date: editItem.end_date || '',
        progress: editItem.progress,
        is_complete: editItem.is_complete,
        is_included: editItem.is_included,
        assigned_to: editItem.assigned_to || '',
        assigned_role: editItem.assigned_role || '',
        notes: editItem.notes || '',
        baseline_start_date: editItem.baseline_start_date || '',
        baseline_end_date: editItem.baseline_end_date || '',
        actual_start_date: editItem.actual_start_date || '',
        actual_end_date: editItem.actual_end_date || '',
        delay_reason: editItem.delay_reason || '',
        delay_status: editItem.delay_status,
        schedule_risk_level: editItem.schedule_risk_level,
        is_critical: editItem.is_critical,
        bar_color: editItem.bar_color,
        bar_color_mode: editItem.bar_color_mode,
        gantt_color: editItem.gantt_color || editItem.bar_color || null,
      });
    } else {
      setForm({
        title: '', phase: '', main_stage: '', construction_sub_stage: '', duration_days: 1, start_date: '', end_date: '',
        progress: 0, is_complete: false, is_included: true, assigned_to: '', assigned_role: '', notes: '',
        baseline_start_date: '', baseline_end_date: '', actual_start_date: '', actual_end_date: '',
        delay_reason: '', delay_status: 'on_track', schedule_risk_level: 'normal', is_critical: false,
        bar_color: null, bar_color_mode: 'phase', gantt_color: null,
        ...(createDefaults || {}),
      });
    }
    setChangeReason('');
    setPublishToClient(false);
    setStartDateWarning(null);
    setEndDateWarning(null);
    setShowDurationRecalcConfirm(false);
    setOpenSections(new Set(['basic', 'color', 'dates', ...(editItem ? [] : ['links'])]));
    setShowCompletionBlock(false);
    setPendingCreateLink(null);
  }, [show, editItem, createDefaults]);

  // Reflect dependency recalculation dates in the open modal without resetting other fields.
  useEffect(() => {
    if (!show || !editItem) return;
    setForm((prev) => ({
      ...prev,
      start_date: editItem.start_date || '',
      end_date: editItem.end_date || '',
    }));
  }, [show, editItem?.id, editItem?.start_date, editItem?.end_date]);

  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isOpen = useCallback((key: SectionKey) => openSections.has(key), [openSections]);

  if (!show) return null;

  const handleDurationChange = (days: number) => {
    const newEnd = form.start_date
      ? addWorkingDays(form.start_date, Math.max(1, days) - 1, settings, holidays)
      : form.end_date;
    setForm((p) => ({ ...p, duration_days: Math.max(1, days), end_date: newEnd }));
  };

  const handleStartDateChange = (start: string) => {
    setStartDateWarning(null);
    if (start && !isWorkingDay(start, settings, holidays)) {
      const day = getDayName(start);
      setStartDateWarning(t('scheduleModal.notWorkingDay', { day }));
    }
    const newEnd = start && form.duration_days > 0
      ? addWorkingDays(start, form.duration_days - 1, settings, holidays)
      : form.end_date;
    setForm((p) => ({ ...p, start_date: start, end_date: newEnd }));
  };

  const handleEndDateChange = (end: string) => {
    setEndDateWarning(null);
    if (end && !isWorkingDay(end, settings, holidays)) {
      const day = getDayName(end);
      setEndDateWarning(t('scheduleModal.notWorkingDay', { day }));
    }
    setForm((p) => ({ ...p, end_date: end }));
  };

  const isSignificantChange = (): boolean => {
    if (!editItem) return false;
    const datesChanged =
      (form.start_date || null) !== (editItem.start_date || null) ||
      (form.end_date || null) !== (editItem.end_date || null);
    const durationChanged = form.duration_days !== editItem.duration_days;
    const completionChanged = form.is_complete !== editItem.is_complete;
    const delayedNow = form.delay_status === 'delayed' || form.delay_status === 'completed_late';
    const wasDelayed = editItem.delay_status === 'delayed' || editItem.delay_status === 'completed_late';
    return datesChanged || durationChanged || completionChanged || delayedNow || wasDelayed || editItem.is_critical;
  };

  const handleCompleteToggle = (checked: boolean) => {
    if (!canEdit || !editItem) return;
    if (checked) {
      const cp = checklistSectionProgress;
      // Always show completion modal when marking complete with linked checklist
      if (cp && cp.hasLinkedItems) {
        setShowCompletionBlock(true);
        return;
      }
      // No checklist linked — management can override, others blocked
      if (!isManagement) {
        setShowCompletionBlock(true);
        return;
      }
    }
    setForm((p) => ({ ...p, is_complete: checked, progress: checked ? 100 : (p.progress === 100 ? 0 : p.progress) }));
  };

  const handleManualComplete = () => {
    setForm((p) => ({ ...p, is_complete: true, progress: 100 }));
    setShowCompletionBlock(false);
  };

  const handleSubmit = (action: 'save' | 'save-only' | 'save-recalc' = 'save') => {
    if (!form.title.trim() || saving || !canEdit) return;
    // Show confirmation modal only for the initial Save button click
    if (action === 'save' && editItem && form.duration_days !== editItem.duration_days && hasSuccessors) {
      setShowDurationRecalcConfirm(true);
      return;
    }
    onSave({
      form,
      changeReason,
      publishToClient,
      skipRecalcConfirm: action === 'save-only',
      action: 'save',
      pendingLink: !editItem ? pendingCreateLink : null,
    });
  };

  const handleCreateTask = () => {
    onSave({ form, changeReason, publishToClient, skipRecalcConfirm: false, action: 'create-task' });
  };

  const handleSendInstruction = () => {
    setShowInstructionModal(true);
  };

  const predCount = editItem ? links.filter((l) => l.successor_item_id === editItem.id).length : 0;
  const succCount = editItem ? links.filter((l) => l.predecessor_item_id === editItem.id).length : 0;
  const linkBadge = predCount + succCount;

  return (
    <div className="fixed inset-0 z-[60] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full lg:max-w-2xl rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h3 className="text-[#1A1A1A] font-bold text-base">
            {editItem ? t('scheduleModal.editItem') : t('scheduleModal.addItem')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-2 space-y-1 min-h-0">
          {/* Section 1: Basic Info */}
          <div className="border-b border-gray-100">
            <SectionHeader title={t('scheduleModal.basicInfo')} isOpen={isOpen('basic')} onToggle={() => toggleSection('basic')} />
            {isOpen('basic') && (
              <div className="pb-4 space-y-3">
                <div>
                  <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.titleLabel')}</label>
                  <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Foundation excavation" readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] ${!canEdit ? 'bg-gray-50' : ''}`} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.phaseLabel')}</label>
                    <input type="text" value={form.phase} onChange={(e) => setForm((p) => ({ ...p, phase: e.target.value }))} placeholder="e.g. Foundations" readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] ${!canEdit ? 'bg-gray-50' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.stageLabel')}</label>
                    <select value={form.main_stage} onChange={(e) => setForm((p) => ({ ...p, main_stage: e.target.value, construction_sub_stage: e.target.value === 'Construction' ? p.construction_sub_stage : '' }))} disabled={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] ${!canEdit ? 'bg-gray-50' : ''}`}>
                      <option value="">{t('scheduleModal.selectStage')}</option>
                      <option value="Planning">{t('stage.Planning')}</option>
                      <option value="Design">{t('stage.Design')}</option>
                      <option value="Consent">{t('stage.Consent')}</option>
                      <option value="Construction">{t('stage.Construction')}</option>
                      <option value="Handover">{t('stage.Handover')}</option>
                    </select>
                  </div>
                  {form.main_stage === 'Construction' && (
                    <div>
                      <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.constructionSubStage')}</label>
                      <select value={form.construction_sub_stage} onChange={(e) => setForm((p) => ({ ...p, construction_sub_stage: e.target.value }))} disabled={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] ${!canEdit ? 'bg-gray-50' : ''}`}>
                        <option value="">{t('scheduleModal.selectSubStage')}</option>
                        <option value="Base">Base (15% payment)</option>
                        <option value="Frame">Frame (25% payment)</option>
                        <option value="Lock Up">Lock Up (25% payment)</option>
                        <option value="Fixing">Fixing (20% payment)</option>
                        <option value="Completion">Completion (NIL)</option>
                        <option value="Settlement">Settlement (10% payment)</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.durationLabel')}</label>
                    <input type="number" min={1} max={999} value={form.duration_days} onChange={(e) => handleDurationChange(Number(e.target.value))} readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] ${!canEdit ? 'bg-gray-50' : ''}`} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.assignedRole')}</label>
                    <input type="text" value={form.assigned_role} onChange={(e) => setForm((p) => ({ ...p, assigned_role: e.target.value }))} placeholder="e.g. Framer" readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] ${!canEdit ? 'bg-gray-50' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.assignedTo')}</label>
                    <input type="text" value={form.assigned_to} onChange={(e) => setForm((p) => ({ ...p, assigned_to: e.target.value }))} placeholder="e.g. John Smith" readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] ${!canEdit ? 'bg-gray-50' : ''}`} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Gantt Bar Color — prominent near top */}
          {isManagement && (
            <div className="border-b border-gray-100">
              <SectionHeader title={t('scheduleModal.ganttBarColor')} isOpen={isOpen('color')} onToggle={() => toggleSection('color')} />
              {isOpen('color') && (
                <div className="pb-4">
                  <ScheduleItemModalColorSection mode={form.bar_color_mode} color={form.bar_color} canEdit={canEdit} onChange={(mode, color) => setForm((p) => ({ ...p, bar_color_mode: mode, bar_color: color, gantt_color: mode === 'custom' ? color : null }))} />
                </div>
              )}
            </div>
          )}

          {/* Section 2: Dates & Progress */}
          <div className="border-b border-gray-100">
            <SectionHeader title={t('scheduleModal.datesProgress')} isOpen={isOpen('dates')} onToggle={() => toggleSection('dates')} />
            {isOpen('dates') && (
              <div className="pb-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.startDate')}</label>
                    <ScheduleDateInput
                      value={form.start_date}
                      onChange={handleStartDateChange}
                      readOnly={!canEdit}
                      className={`w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] block ${!canEdit ? 'bg-gray-50' : ''}`}
                    />
                    {startDateWarning && canEdit && (
                      <div className="mt-2 bg-[#FEF0E7] rounded-xl p-2.5 border border-[#FDE68A]">
                        <div className="flex items-start gap-2">
                          <i className="ri-error-warning-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                          <p className="text-[#92400E] text-xs">{startDateWarning}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <button onClick={() => { const working = nextWorkingDay(form.start_date, settings, holidays); const newEnd = form.duration_days > 0 ? addWorkingDays(working, form.duration_days - 1, settings, holidays) : form.end_date; setForm((p) => ({ ...p, start_date: working, end_date: newEnd })); setStartDateWarning(null); }} className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-[11px] font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap">{t('scheduleModal.moveToNextWorkingDay')}</button>
                          <button onClick={() => setStartDateWarning(null)} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-[11px] font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap">{t('scheduleModal.keepThisDate')}</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.endDate')}</label>
                    <ScheduleDateInput
                      value={form.end_date}
                      onChange={handleEndDateChange}
                      readOnly={!canEdit}
                      className={`w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] block ${!canEdit ? 'bg-gray-50' : ''}`}
                    />
                    {endDateWarning && canEdit && (
                      <div className="mt-2 bg-[#FEF0E7] rounded-xl p-2.5 border border-[#FDE68A]">
                        <div className="flex items-start gap-2">
                          <i className="ri-error-warning-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                          <p className="text-[#92400E] text-xs">{endDateWarning}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <button onClick={() => { const working = shiftToWorkingDay(form.end_date, settings, holidays); setForm((p) => ({ ...p, end_date: working })); setEndDateWarning(null); }} className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-[11px] font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap">{t('scheduleModal.moveToNextWorkingDay')}</button>
                          <button onClick={() => setEndDateWarning(null)} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-[11px] font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap">{t('scheduleModal.keepThisDate')}</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Checklist-driven progress display */}
                {checklistSectionProgress && checklistSectionProgress.hasLinkedItems && (
                  <div className="bg-[#F9FAFB] rounded-xl p-3 border border-gray-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#555555]">{t('scheduleModal.checklistProgress')}</span>
                      <span className="text-xs font-bold text-[#E8621A]">{checklistSectionProgress.progressPct}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#E8621A] transition-all"
                        style={{ width: `${checklistSectionProgress.progressPct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#9CA3AF]">
                      <span>
                        {t('scheduleModal.completed')}: <span className="font-semibold text-[#374151]">{checklistSectionProgress.completedRequiredCount} / {checklistSectionProgress.requiredCount}</span>
                      </span>
                      {checklistSectionProgress.missingManagement > 0 && (
                        <span className="text-[#F59E0B] font-semibold">{checklistSectionProgress.missingManagement} {t('stages.mgmtPendingLabel')}</span>
                      )}
                      {checklistSectionProgress.missingSubcontractor > 0 && (
                        <span className="text-[#F59E0B] font-semibold">{checklistSectionProgress.missingSubcontractor} {t('stages.subPendingLabel')}</span>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.progressLabel')}</label>
                  <input type="range" min={0} max={100} value={form.progress} onChange={(e) => setForm((p) => ({ ...p, progress: Number(e.target.value) }))} disabled={!canEdit || (checklistSectionProgress?.hasLinkedItems ?? false)} className="w-full accent-[#E8621A]" />
                  <div className="flex justify-between text-xs text-[#9CA3AF] mt-1">
                    <span>0%</span>
                    <span className="font-bold text-[#E8621A]">{form.progress}%</span>
                    <span>100%</span>
                  </div>
                  {checklistSectionProgress?.hasLinkedItems && (
                    <p className="text-[10px] text-[#9CA3AF] mt-1">
                      {t('scheduleModal.progressDerivedFromChecklist')}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.baselineStart')}</label>
                    <div className={`w-full min-w-0 border rounded-xl px-3 py-2.5 text-sm block ${form.baseline_start_date ? 'bg-[#F5F3FF] border-[#DDD6FE] text-[#5B21B6]' : 'bg-gray-50 border-gray-200 text-[#9CA3AF]'}`}>{form.baseline_start_date ? formatDateFull(form.baseline_start_date) : t('scheduleModal.notSet')}</div>
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.baselineEnd')}</label>
                    <div className={`w-full min-w-0 border rounded-xl px-3 py-2.5 text-sm block ${form.baseline_end_date ? 'bg-[#F5F3FF] border-[#DDD6FE] text-[#5B21B6]' : 'bg-gray-50 border-gray-200 text-[#9CA3AF]'}`}>{form.baseline_end_date ? formatDateFull(form.baseline_end_date) : t('scheduleModal.notSet')}</div>
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.actualStart')}</label>
                    <ScheduleDateInput
                      value={form.actual_start_date}
                      onChange={(iso) => setForm((p) => ({ ...p, actual_start_date: iso }))}
                      readOnly={!canEdit}
                      className={`w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] block ${!canEdit ? 'bg-gray-50' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.actualEnd')}</label>
                    <ScheduleDateInput
                      value={form.actual_end_date}
                      onChange={(iso) => setForm((p) => ({ ...p, actual_end_date: iso }))}
                      readOnly={!canEdit}
                      className={`w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] block ${!canEdit ? 'bg-gray-50' : ''}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.delayStatus')}</label>
                    <div className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] flex items-center gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: getDelayStatusColor(form.delay_status).bg, color: getDelayStatusColor(form.delay_status).text }}>{getDelayStatusLabel(form.delay_status)}</span>
                      <span className="text-[10px] text-[#9CA3AF]">{t('scheduleModal.auto')}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.riskLevel')}</label>
                    <div className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] flex items-center gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: getRiskLevelBg(form.schedule_risk_level), color: getRiskLevelColor(form.schedule_risk_level) }}>{form.schedule_risk_level}</span>
                      <span className="text-[10px] text-[#9CA3AF]">{t('scheduleModal.auto')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <label className={`flex items-center gap-2 cursor-pointer text-sm text-[#374151] ${!canEdit ? 'cursor-default' : ''}`}>
                    <input type="checkbox" checked={form.is_complete} onChange={(e) => handleCompleteToggle(e.target.checked)} disabled={!canEdit} className="w-4 h-4 accent-[#E8621A]" />
                    {t('scheduleModal.complete')}
                    {checklistSectionProgress && !checklistSectionProgress.allRequiredComplete && checklistSectionProgress.hasLinkedItems && (
                      <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">
                        <i className="ri-lock-line mr-0.5"></i>{t('scheduleModal.locked')}
                      </span>
                    )}
                    {(!checklistSectionProgress || !checklistSectionProgress.hasLinkedItems) && (
                      <span className="text-[9px] font-bold text-[#9CA3AF] bg-gray-100 px-1.5 py-0.5 rounded-full">
                        {t('scheduleModal.noChecklist')}
                      </span>
                    )}
                  </label>
                  <label className={`flex items-center gap-2 cursor-pointer text-sm text-[#374151] ${!canEdit ? 'cursor-default' : ''}`}>
                    <input type="checkbox" checked={form.is_included} onChange={(e) => setForm((p) => ({ ...p, is_included: e.target.checked }))} disabled={!canEdit} className="w-4 h-4 accent-[#E8621A]" />
                    {t('scheduleModal.includedInProject')}
                  </label>
                  <label className={`flex items-center gap-2 cursor-pointer text-sm text-[#374151] ${!canEdit ? 'cursor-default' : ''}`}>
                    <input type="checkbox" checked={form.is_critical} onChange={(e) => setForm((p) => ({ ...p, is_critical: e.target.checked }))} disabled={!canEdit} className="w-4 h-4 accent-[#E8621A]" />
                    {t('scheduleModal.criticalPath')}
                  </label>
                </div>

                {/* Completion readiness indicator */}
                {checklistSectionProgress && checklistSectionProgress.hasLinkedItems && (
                  <div className={`rounded-xl p-3 border ${checklistSectionProgress.allRequiredComplete ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF0E7] border-[#FDBA74]'}`}>
                    <div className="flex items-center gap-2">
                      <i className={`${checklistSectionProgress.allRequiredComplete ? 'ri-checkbox-circle-line text-[#059669]' : 'ri-error-warning-line text-[#F59E0B]'} text-sm`}></i>
                      <p className={`text-xs font-semibold ${checklistSectionProgress.allRequiredComplete ? 'text-[#059669]' : 'text-[#92400E]'}`}>
                        {checklistSectionProgress.allRequiredComplete
                          ? t('scheduleModal.readyToComplete')
                          : t('scheduleModal.notReady', { completed: checklistSectionProgress.completedRequiredCount, total: checklistSectionProgress.requiredCount })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Create-mode dependency links */}
          {!editItem && isManagement && canEdit && (
            <div className="border-b border-gray-100 pb-4">
              <SectionHeader
                title={t('scheduleModal.linkedScheduleItems')}
                isOpen={isOpen('links')}
                onToggle={() => toggleSection('links')}
              />
              {isOpen('links') && (
                <ScheduleItemModalCreateLinkSection
                  scheduleItems={scheduleItems ?? includedItems}
                  draft={pendingCreateLink}
                  onChange={setPendingCreateLink}
                  disabled={saving}
                />
              )}
            </div>
          )}

          {/* Section 3: Linked Schedule Items */}
          {editItem && isManagement && (
            <div className="border-b border-gray-100">
              <SectionHeader title={t('scheduleModal.linkedScheduleItems')} isOpen={isOpen('links')} onToggle={() => toggleSection('links')} badge={linkBadge} />
              {isOpen('links') && (
                <div className="pb-4">
                  <ScheduleItemModalLinksSection
                    editItem={editItem}
                    links={links}
                    scheduleItems={scheduleItems ?? includedItems}
                    canEdit={canEdit}
                    savingLink={savingLink}
                    deletingLinkId={deletingLinkId}
                    onUpsertLink={onUpsertLink}
                    onRemoveLink={onRemoveLink}
                    onRecalcLinkedItems={onRecalcLinkedItems}
                  />
                </div>
              )}
            </div>
          )}

          {/* Section: Linked Checklist Items */}
          {editItem && isManagement && (
            <div className="border-b border-gray-100">
              <SectionHeader title={t('scheduleModal.linkedChecklistItems')} isOpen={isOpen('checklist')} onToggle={() => toggleSection('checklist')} />
              {isOpen('checklist') && (
                <div className="pb-4">
                  <ScheduleItemModalChecklistSection
                    scheduleItemId={editItem.id}
                    sourceTemplateItemId={editItem.source_template_item_id}
                    projectId={projectId}
                    canManage={isManagement}
                    onChecklistChanged={onChecklistChanged}
                  />
                </div>
              )}
            </div>
          )}

          {/* Section: Checklist Completion */}
          {editItem && checklistSectionProgress && checklistSectionProgress.hasLinkedItems && (
            <div className="border-b border-gray-100">
              <SectionHeader title={t('scheduleModal.checklistCompletionSummary')} isOpen={isOpen('checklist-progress')} onToggle={() => toggleSection('checklist-progress')} />
              {isOpen('checklist-progress') && (
                <div className="pb-4 space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.totalLinked')}</p>
                      <p className="text-sm font-bold text-[#1A1A1A]">{checklistSectionProgress.totalLinked}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.completed')}</p>
                      <p className="text-sm font-bold text-[#059669]">{checklistSectionProgress.completedRequiredCount}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.progress')}</p>
                      <p className="text-sm font-bold text-[#E8621A]">{checklistSectionProgress.progressPct}%</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.mgmtChecked')}</p>
                      <p className="text-sm font-bold text-[#E8621A]">{checklistSectionProgress.managementCheckedCount}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.subChecked')}</p>
                      <p className="text-sm font-bold text-[#0891B2]">{checklistSectionProgress.subcontractorCheckedCount}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('scheduleModal.missing')}</p>
                      <p className="text-sm font-bold text-[#EF4444]">
                        {checklistSectionProgress.missingManagement + checklistSectionProgress.missingSubcontractor}
                      </p>
                    </div>
                  </div>

                  {checklistSectionProgress.missingManagement > 0 && (
                    <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74]">
                      <p className="text-[10px] font-semibold text-[#92400E] mb-1">
                        <i className="ri-error-warning-line mr-1"></i>{t('scheduleModal.mgmtChecksPending')}
                      </p>
                      <p className="text-xs text-[#92400E]">{t('scheduleModal.mgmtChecksPendingDesc', { count: checklistSectionProgress.missingManagement })}</p>
                    </div>
                  )}
                  {checklistSectionProgress.missingSubcontractor > 0 && (
                    <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74]">
                      <p className="text-[10px] font-semibold text-[#92400E] mb-1">
                        <i className="ri-error-warning-line mr-1"></i>{t('scheduleModal.subChecksPending')}
                      </p>
                      <p className="text-xs text-[#92400E]">{t('scheduleModal.subChecksPendingDesc', { count: checklistSectionProgress.missingSubcontractor })}</p>
                    </div>
                  )}

                  <button
                    onClick={() => toggleSection('checklist')}
                    className="w-full py-2.5 rounded-xl border border-[#E0DDD8] text-[#6B7280] text-xs font-semibold cursor-pointer whitespace-nowrap hover:border-[#E8621A] hover:text-[#E8621A] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-file-list-3-line"></i>{t('scheduleModal.openLinkedChecklist')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Section 5: Notes & Task */}
          <div className="border-b border-gray-100">
            <SectionHeader title={t('scheduleModal.notesTask')} isOpen={isOpen('notes')} onToggle={() => toggleSection('notes')} />
            {isOpen('notes') && (
              <div className="pb-4 space-y-3">
                <div>
                  <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.notesLabel')}</label>
                  <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Add details..." rows={2} maxLength={500} readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none ${!canEdit ? 'bg-gray-50' : ''}`} />
                </div>
                <div>
                  <label className="block text-[#555555] text-xs font-semibold mb-1.5">{t('scheduleModal.delayReason')}</label>
                  <textarea value={form.delay_reason} onChange={(e) => setForm((p) => ({ ...p, delay_reason: e.target.value }))} placeholder="e.g. Weather delay, material shortage..." rows={2} maxLength={500} readOnly={!canEdit} className={`w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none ${!canEdit ? 'bg-gray-50' : ''}`} />
                </div>
                {/* Quick actions inside Notes section */}
                {editItem && canEdit && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <button onClick={handleCreateTask} disabled={saving} className="flex-1 py-2.5 rounded-xl border-2 border-dashed border-[#E0DDD8] text-[#6B7280] text-xs font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap hover:border-[#E8621A] hover:text-[#E8621A] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                      <i className="ri-task-line"></i>{t('scheduleModal.createTask')}
                    </button>
                    <button onClick={handleSendInstruction} disabled={saving} className="flex-1 py-2.5 rounded-xl border-2 border-dashed border-[#E0DDD8] text-[#059669] text-xs font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap hover:border-[#059669] hover:bg-[#F0FDF4] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                      <i className="ri-send-plane-line"></i>{t('scheduleModal.sendInstruction')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Change Reason */}
          {editItem && canEdit && isSignificantChange() && (
            <div className="pt-2 pb-2">
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                {t('scheduleModal.reasonForChange')}<span className="text-[#9CA3AF] font-normal ml-1">{t('scheduleModal.reasonOptional')}</span>
              </label>
              <textarea value={changeReason} onChange={(e) => setChangeReason(e.target.value)} placeholder="Example: Inspection delayed, weather impact, subcontractor availability..." rows={2} maxLength={500} className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none" />
            </div>
          )}

          {/* Publish to Client */}
          {editItem && canEdit && isSignificantChange() && (
            <div className="pt-2 pb-2">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={publishToClient} onChange={(e) => setPublishToClient(e.target.checked)} className="w-4 h-4 accent-[#E8621A] mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-[#374151]">{t('scheduleModal.publishToClient')}</span>
                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t('scheduleModal.publishDesc')}</p>
                </div>
              </label>
            </div>
          )}

          {/* Messages */}
          {saveSuccess && (
            <div className="flex items-start gap-2 bg-[#F0FDF4] rounded-xl p-3 border border-[#BBF7D0]">
              <i className="ri-check-line text-[#059669] text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-[#059669] text-sm font-medium">{saveSuccess}</p>
            </div>
          )}
          {saveError && (
            <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{saveError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          {/* Mobile: stack vertically; desktop: row with Save prominent */}
          <div className="flex flex-col sm:flex-row gap-2">
            {editItem && canEdit && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                className="sm:order-1 py-3 rounded-xl border border-[#FCA5A5] text-[#EF4444] text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap hover:bg-[#FEF2F2] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                {t('scheduleModal.deleteItem')}
              </button>
            )}
            <button
              onClick={onClose}
              disabled={saving}
              className="sm:order-2 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('scheduleModal.cancelBtn')}
            </button>
            <button
              onClick={() => handleSubmit('save')}
              disabled={!form.title.trim() || saving || !canEdit}
              className="sm:order-3 sm:flex-[2] py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t('scheduleModal.saving')}
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  {editItem ? t('scheduleModal.saveChanges') : t('scheduleModal.addItemBtn')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Completion Block Modal */}
      {showCompletionBlock && editItem && checklistSectionProgress && (
        <ScheduleCompletionBlockModal
          show={showCompletionBlock}
          itemTitle={editItem.title}
          progress={checklistSectionProgress}
          onConfirmManualComplete={handleManualComplete}
          onClose={() => setShowCompletionBlock(false)}
          canManage={isManagement}
        />
      )}

      {/* Duration Recalc Confirmation */}
      {showDurationRecalcConfirm && editItem && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDurationRecalcConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('scheduleModal.recalcDownstream')}</h3>
              <button onClick={() => setShowDurationRecalcConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">{t('scheduleModal.recalcDesc')}</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button onClick={() => { setShowDurationRecalcConfirm(false); handleSubmit('save-only'); }} className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap">{t('scheduleModal.saveOnly')}</button>
              <button onClick={() => { setShowDurationRecalcConfirm(false); handleSubmit('save-recalc'); }} disabled={saving} className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>...</> : t('scheduleModal.saveAndRecalc')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instruction Create Modal */}
      {showInstructionModal && editItem && (
        <NewInstructionModal
          show={showInstructionModal}
          onClose={() => setShowInstructionModal(false)}
          projectId={projectId}
          linkedScheduleItemId={editItem.id}
          defaultTitle={`${t('scheduleModal.actionRequired')}${editItem.title}`}
          source="schedule-item-modal"
          onSuccess={() => {
            setShowInstructionModal(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && editItem && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base flex items-center gap-2">
                <i className="ri-delete-bin-line text-[#EF4444]"></i>
                {t('scheduleModal.deleteItemTitle')}
              </h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('scheduleModal.deleteItemConfirm')}
              </p>
              <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA]">
                <p className="text-[#1A1A1A] text-sm font-semibold">{editItem.title}</p>
                {editItem.phase && <p className="text-[#9CA3AF] text-xs mt-0.5">{editItem.phase}</p>}
              </div>
              <p className="text-[#92400E] text-xs bg-[#FEF0E7] rounded-xl p-2.5">
                <i className="ri-alert-line mr-1"></i>
                {t('scheduleModal.deleteItemWarning')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('scheduleModal.cancelBtn')}
              </button>
              <button
                onClick={() => {
                  if (onDelete && editItem) {
                    onDelete(editItem.id);
                    setShowDeleteConfirm(false);
                    onClose();
                  }
                }}
                className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>{t('scheduleModal.deleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
